import React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import {
  fetchOneFormulaActive,
  fetchFormulaStatus,
  fetchNameCompletionActive,
  fetchNameCompletionStatus,
} from "../../api/petFormulaApi";
import {
  parseApiDataToOneFormula,
  parseApiDataToCoreBrands,
  parseApiDataToNameCompletion,
} from "./utils";
import { createCoreBrandsHandlers } from "./coreBrandsHandlers";
import { createOneFormulaHandlers } from "./oneFormulaHandlers";
import { createNameCompletionHandlers } from "./nameCompletionHandlers";
import {
  OneFormulaData,
  CoreBrandData,
  NameCompletionRowData,
  UserData,
  WorkspaceData,
  RowSelection,
} from "../../components/SettingsPage/types";
import { setOneFormulaDefaultDraft, setPipelinePending } from "../../slices/settingsSlice";
import SuccessNotification from "../../components/SettingsPage/SuccessNotification";
import ErrorNotification from "../../components/SettingsPage/ErrorNotification";

// 🎯 TypeScript types (we'll keep these in the main file for now)
// But define the mock data structures here

export const useSettings = () => {
  // 🔧 Message API
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();

  // 🔧 Edit state
  const [isEditAllMode, setIsEditAllMode] = useState<boolean>(false);
  const [isEditingOneFormula, setIsEditingOneFormula] = useState<boolean>(false);
  const [editingOneFormulaData, setEditingOneFormulaData] = useState<{
    [key: string]: Partial<OneFormulaData>;
  }>({});

  // 🔧 Core Brands edit state
  const [isEditingCoreBrands, setIsEditingCoreBrands] = useState<boolean>(false);
  const [editingCoreBrandsData, setEditingCoreBrandsData] = useState<{
    [key: string]: Partial<CoreBrandData>;
  }>({});
  const [editingCoreBrandsRows, setEditingCoreBrandsRows] = useState<{ [key: string]: boolean }>(
    {}
  );

  const { userData, selectedWorkspace }: { userData: UserData; selectedWorkspace: WorkspaceData } =
    useSelector((state: any) => state.auth);
  const oneFormulaDefaultDraft = useSelector(
    (state: any) => state.settings?.oneFormulaDefaultDraft
  );
  const pipelinePending = useSelector((state: any) => state.settings?.pipelinePending ?? false);

  // 🔄 Loading state
  const [loading, setLoading] = useState<boolean>(true);
  const [isPipelineRunning, setIsPipelineRunning] = useState<boolean>(false);
  // Declared up here so the oneFormulaHandlers useMemo (below) can plumb the setter
  // down to the merged-save path; the NC poll effect/ref still live in the NC section.
  const [isNcPipelineRunning, setIsNcPipelineRunning] = useState<boolean>(false);
  const [pipelineResult, setPipelineResult] = useState<"success" | "failed" | null>(null);
  const [versionCount, setVersionCount] = useState<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 🔄 Poll formula status while pipeline is running
  useEffect(() => {
    if (!isPipelineRunning) return;

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const poll = async () => {
      try {
        const data = await fetchFormulaStatus(userData, selectedWorkspace);
        if (data.status === "done") {
          stopPolling();
          setIsPipelineRunning(false);
          setPipelineResult("success");
          dispatch(setPipelinePending(false));

          messageApi.success({
            key: "pet-formula-update",
            content: React.createElement(
              "div",
              { className: "custom-success-message-wrapper" },
              React.createElement(SuccessNotification, {
                message: "Configuration updated successfully!",
              })
            ),
            duration: 5,
            icon: React.createElement("div"),
            className: "pet-formula-success-message",
          });

          // Refresh table to show latest data from the server
          try {
            const freshData = await fetchOneFormulaActive(userData, selectedWorkspace);
            setRawCoreBrandsApiData(freshData);
            setApiOneFormulaData(parseApiDataToOneFormula(freshData));
            setDisplayCoreBrandsData(parseApiDataToCoreBrands(freshData));
          } catch {
            // Data refresh failed; table already shows optimistic data
          }

          setTimeout(() => setPipelineResult(null), 5000);
        } else if (data.status === "failed") {
          stopPolling();
          setIsPipelineRunning(false);
          setPipelineResult("failed");
          dispatch(setPipelinePending(false));

          messageApi.error({
            key: "pet-formula-update",
            content: React.createElement(
              "div",
              { className: "custom-error-message-wrapper" },
              React.createElement(ErrorNotification, {
                message: "Configuration update failed. Please try again.",
              })
            ),
            duration: 5,
            icon: React.createElement("div"),
            className: "pet-formula-error-message",
          });

          setTimeout(() => setPipelineResult(null), 5000);
        }
        // status === "pending" → keep polling
      } catch {
        // ignore transient errors, keep polling
      }
    };

    poll(); // immediate first check
    pollIntervalRef.current = setInterval(poll, 30_000);

    return stopPolling;
  }, [isPipelineRunning, userData, selectedWorkspace]);

  // 🔄 Persist pipeline running state to Redux so it survives navigation
  useEffect(() => {
    if (isPipelineRunning) {
      dispatch(setPipelinePending(true));
    }
  }, [isPipelineRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔄 Data state
  const [apiOneFormulaData, setApiOneFormulaData] = useState<OneFormulaData[]>([]);
  const [displayCoreBrandsData, setDisplayCoreBrandsData] = useState<CoreBrandData[]>([]); // parsed for table display

  // 🔄 The unparsed API response, kept alongside the table-shaped copies above
  const [rawCoreBrandsApiData, setRawCoreBrandsApiData] = useState<any>(null);

  // 🎛️ Row selection
  const [selectedOneFormulaRows, setSelectedOneFormulaRows] = useState<string[]>([]);
  const [selectedCoreBrandsRows, setSelectedCoreBrandsRows] = useState<string[]>([]);

  const persistDefaultRowDraft = (rowData: OneFormulaData) => {
    dispatch(setOneFormulaDefaultDraft(rowData));
  };

  const applyDefaultDraftToData = (data: OneFormulaData[], draft: OneFormulaData | null) => {
    if (!draft) {
      return data;
    }
    return data.map((item) => (item.market === "Default Value" ? { ...item, ...draft } : item));
  };

  const defaultRowFields: (keyof OneFormulaData)[] = [
    "marketPenetration",
    "marketPenetrationOfClient",
    "avgCoreSkuClient",
    "comboPresence",
    "fixedComboPresence",
    "optionComboPresence",
    "nameAccuracy",
    "avgImagePresence",
    "editBy",
    "lastEditTime",
  ];

  // 🔄 Load the ONE Performance formula data
  useEffect(() => {
    const loadOneFormulaData = async () => {
      try {
        setLoading(true);
        const apiResponse = await fetchOneFormulaActive(userData, selectedWorkspace);

        setRawCoreBrandsApiData(apiResponse);

        const parsedOneFormula = parseApiDataToOneFormula(apiResponse);
        const parsedCoreBrands = parseApiDataToCoreBrands(apiResponse);

        setApiOneFormulaData(parsedOneFormula);
        setDisplayCoreBrandsData(parsedCoreBrands);

        // 🔄 Check if pipeline is still running (e.g. user navigated away and came back)
        try {
          const statusData = await fetchFormulaStatus(userData, selectedWorkspace);
          setVersionCount(statusData.version_count ?? 0);
          if (statusData.status === "pending") {
            setIsPipelineRunning(true);
          } else if (statusData.status === "done" && pipelinePending) {
            // Pipeline completed while user was away — show success and clear pending flag
            dispatch(setPipelinePending(false));
            setPipelineResult("success");
            setTimeout(() => setPipelineResult(null), 5000);
            messageApi.success({
              key: "pet-formula-update",
              content: React.createElement(
                "div",
                { className: "custom-success-message-wrapper" },
                React.createElement(SuccessNotification, {
                  message: "Configuration updated successfully!",
                })
              ),
              duration: 5,
              icon: React.createElement("div"),
              className: "pet-formula-success-message",
            });
            // Data is already fresh from fetchOneFormulaActive above
          }
        } catch {
          // status check is best-effort; ignore errors
        }
      } catch (error) {
        console.error("[SettingsPage] Failed to load PET Formula data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userData) {
      loadOneFormulaData();
    } else {
      // Without user data there is nothing to fetch, so drop the loading state
      // and let the page fall back to its mock data.
      setLoading(false);
    }
  }, [userData, selectedWorkspace]);

  useEffect(() => {
    if (!oneFormulaDefaultDraft || apiOneFormulaData.length === 0) {
      return;
    }
    const currentDefaultRow = apiOneFormulaData.find((item) => item.market === "Default Value");
    if (!currentDefaultRow) {
      return;
    }
    const hasDiff = defaultRowFields.some(
      (field) => currentDefaultRow[field] !== oneFormulaDefaultDraft[field]
    );
    if (!hasDiff) {
      return;
    }
    setApiOneFormulaData((prev) => applyDefaultDraftToData(prev, oneFormulaDefaultDraft));
  }, [apiOneFormulaData, oneFormulaDefaultDraft]);

  // Memoize handler creators to prevent creating new function references on every render
  const oneFormulaHandlers = useMemo(
    () =>
      createOneFormulaHandlers({
        isEditingOneFormula,
        setIsEditingOneFormula,
        editingOneFormulaData,
        setEditingOneFormulaData,
        isEditingCoreBrands,
        editingCoreBrandsData,
        setEditingCoreBrandsData,
        setIsEditingCoreBrands,
        apiOneFormulaData,
        displayCoreBrandsData,
        setApiOneFormulaData,
        setDisplayCoreBrandsData,
        messageApi,
        userData,
        selectedWorkspace,
        persistDefaultRowDraft,
        setIsPipelineRunning,
        setIsNcPipelineRunning,
      }),
    [
      isEditingOneFormula,
      setIsEditingOneFormula,
      editingOneFormulaData,
      setEditingOneFormulaData,
      isEditingCoreBrands,
      editingCoreBrandsData,
      setEditingCoreBrandsData,
      setIsEditingCoreBrands,
      apiOneFormulaData,
      displayCoreBrandsData,
      setApiOneFormulaData,
      setDisplayCoreBrandsData,
      messageApi,
      userData,
      selectedWorkspace,
      persistDefaultRowDraft,
      setIsPipelineRunning,
      setIsNcPipelineRunning,
    ]
  );

  const {
    handleEditAllOneFormula,
    handleCancelOneFormula,
    validateOneFormulaData,
    handleInputChange,
    handleApplyDefaultToEditingRow,
    handleSaveDefaultValue,
    handleApplyDefaultToAll,
  } = oneFormulaHandlers;

  const coreBrandsHandlers = useMemo(
    () =>
      createCoreBrandsHandlers({
        isEditingCoreBrands,
        setIsEditingCoreBrands,
        editingCoreBrandsData,
        setEditingCoreBrandsData,
        isEditingOneFormula,
        editingOneFormulaData,
        setEditingOneFormulaData,
        setIsEditingOneFormula,
        apiOneFormulaData,
        displayCoreBrandsData,
        setApiOneFormulaData,
        setDisplayCoreBrandsData,
        messageApi,
        userData,
        selectedWorkspace,
        setEditingCoreBrandsRows,
        setIsPipelineRunning,
      }),
    [
      isEditingCoreBrands,
      setIsEditingCoreBrands,
      editingCoreBrandsData,
      setEditingCoreBrandsData,
      isEditingOneFormula,
      editingOneFormulaData,
      setEditingOneFormulaData,
      setIsEditingOneFormula,
      apiOneFormulaData,
      displayCoreBrandsData,
      setApiOneFormulaData,
      setDisplayCoreBrandsData,
      messageApi,
      userData,
      selectedWorkspace,
      setEditingCoreBrandsRows,
      setIsPipelineRunning,
    ]
  );

  const {
    handleEditAllCoreBrands,
    handleCancelCoreBrands,
    handleCoreBrandInputChange,
    handleCoreBrandClearAndCompact,
    handleEditRow: handleEditCoreBrandsRow,
    handleSaveRow: handleSaveCoreBrandsRow,
  } = coreBrandsHandlers;

  // ========== Name Completion state ==========
  const [ncTableData, setNcTableData] = useState<NameCompletionRowData[]>([]);
  const [ncEditingData, setNcEditingData] = useState<{
    [key: string]: Partial<NameCompletionRowData>;
  }>({});
  const [isEditingNc, setIsEditingNc] = useState<boolean>(false);
  const ncPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const loadNcData = async () => {
      if (!userData) return;
      try {
        const apiResponse = await fetchNameCompletionActive(userData, selectedWorkspace);
        setNcTableData(parseApiDataToNameCompletion(apiResponse));
        const statusData = await fetchNameCompletionStatus(userData, selectedWorkspace);
        if (statusData.status === "pending") setIsNcPipelineRunning(true);
      } catch (error) {
        console.error("[SettingsPage] Failed to load Name Completion data:", error);
        messageApi.error("Could not load Name Completion. Reload the page to try again.");
      }
    };
    loadNcData();
  }, [userData, selectedWorkspace]);

  useEffect(() => {
    if (!isNcPipelineRunning) return;
    const stopNcPolling = () => {
      if (ncPollIntervalRef.current) {
        clearInterval(ncPollIntervalRef.current);
        ncPollIntervalRef.current = null;
      }
    };
    const poll = async () => {
      try {
        const data = await fetchNameCompletionStatus(userData, selectedWorkspace);
        if (data.status === "done") {
          stopNcPolling();
          setIsNcPipelineRunning(false);
          try {
            const fresh = await fetchNameCompletionActive(userData, selectedWorkspace);
            setNcTableData(parseApiDataToNameCompletion(fresh));
          } catch (error) {
            console.error("[SettingsPage] Failed to reload Name Completion data:", error);
            messageApi.error(
              "Name Completion finished, but the new data could not load. Reload the page."
            );
          }
        } else if (data.status === "failed") {
          stopNcPolling();
          setIsNcPipelineRunning(false);
        }
      } catch (error) {
        // The next poll retries in 30 seconds, so a failed check only logs.
        console.error("[SettingsPage] Failed to check the Name Completion pipeline status:", error);
      }
    };
    poll();
    ncPollIntervalRef.current = setInterval(poll, 30_000);
    return stopNcPolling;
  }, [isNcPipelineRunning, userData, selectedWorkspace]);

  const ncHandlers = useMemo(
    () =>
      createNameCompletionHandlers({
        tableData: ncTableData,
        setTableData: setNcTableData,
        editingData: ncEditingData,
        setEditingData: setNcEditingData,
        isEditing: isEditingNc,
        setIsEditing: setIsEditingNc,
        setIsPipelineRunning: setIsNcPipelineRunning,
        messageApi,
        userData,
        selectedWorkspace,
      }),
    [ncTableData, ncEditingData, isEditingNc, userData, selectedWorkspace]
  );

  const {
    handleEnterEdit: handleEnterEditNc,
    handleCancel: handleCancelNc,
    handleInputChange: handleNcInputChange,
    handleSaveDefaultValue: handleNcSaveDefaultValue,
    handleApplyDefaultToAll: handleNcApplyDefaultToAll,
    handleApplyDefaultToRow: handleNcApplyDefaultToRow,
    handleApplyDefaultToEditingRow: handleNcApplyDefaultToEditingRow,
    validateData: validateNcData,
  } = ncHandlers;

  // Row selection
  const oneFormulaRowSelection: RowSelection = {
    selectedRowKeys: selectedOneFormulaRows,
    onChange: (selectedRowKeys: string[]) => {
      setSelectedOneFormulaRows(selectedRowKeys);
    },
  };

  const coreBrandsRowSelection: RowSelection = {
    selectedRowKeys: selectedCoreBrandsRows,
    onChange: (selectedRowKeys: string[]) => {
      setSelectedCoreBrandsRows(selectedRowKeys);
    },
  };

  return {
    // 🔄 Message API
    contextHolder,

    // 🔄 Loading state
    loading,
    isPipelineRunning,
    setIsPipelineRunning,
    pipelineResult,
    versionCount,

    // 🔄 Data state
    apiOneFormulaData,
    setApiOneFormulaData,
    displayCoreBrandsData,
    setDisplayCoreBrandsData,
    rawCoreBrandsApiData,
    setRawCoreBrandsApiData,

    // 🔧 Edit state
    isEditAllMode,
    setIsEditAllMode,
    isEditingOneFormula,
    setIsEditingOneFormula,
    editingOneFormulaData,
    setEditingOneFormulaData,
    isEditingCoreBrands,
    setIsEditingCoreBrands,
    editingCoreBrandsData,
    setEditingCoreBrandsData,
    editingCoreBrandsRows,
    setEditingCoreBrandsRows,

    // 🎛️ Selection state
    selectedOneFormulaRows,
    setSelectedOneFormulaRows,
    selectedCoreBrandsRows,
    setSelectedCoreBrandsRows,
    oneFormulaRowSelection,
    coreBrandsRowSelection,

    // 🔧 Edit handlers
    handleEditAllOneFormula,
    handleCancelOneFormula,
    validateOneFormulaData,
    handleInputChange,
    handleApplyDefaultToEditingRow,
    handleSaveDefaultValue,
    handleApplyDefaultToAll,
    handleEditAllCoreBrands,
    handleCancelCoreBrands,
    handleCoreBrandInputChange,
    handleCoreBrandClearAndCompact,
    handleEditCoreBrandsRow,
    handleSaveCoreBrandsRow,

    // 📊 Data parsers
    parseApiDataToOneFormula,
    parseApiDataToCoreBrands,

    // 📡 User data
    userData,

    // ========== Name Completion ==========
    ncTableData,
    setNcTableData,
    ncEditingData,
    setNcEditingData,
    isEditingNc,
    setIsEditingNc,
    isNcPipelineRunning,
    setIsNcPipelineRunning,
    handleEnterEditNc,
    handleCancelNc,
    handleNcInputChange,
    handleNcSaveDefaultValue,
    handleNcApplyDefaultToAll,
    handleNcApplyDefaultToRow,
    handleNcApplyDefaultToEditingRow,
    validateNcData,
  };
};
